import { protectInitialAuthLink } from './authLinkParameters'

// Analytics is opt-in for the beta.  A hard-coded production property made
// every local/test deployment attempt to load a third-party script even when
// tracking had not been configured.
export const GA_TRACKING_ID = import.meta.env.VITE_GA_ID?.trim()
const MARKETING_ATTRIBUTION_STORAGE_KEY = 'sugarm.marketing-attribution'
const MARKETING_ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
] as const
const MAX_ATTRIBUTION_VALUE_LENGTH = 100

export type MarketingAttribution = Partial<Record<(typeof MARKETING_ATTRIBUTION_KEYS)[number], string>>

let hasLoadedAnalytics = false

const sanitizeAnalyticsUrl = (value: string): string => {
  if (!value) return ''

  try {
    const url = new URL(value, window.location.origin)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return ''

    // Aggregate member-page traffic without sending member identifiers.
    const pathname = url.pathname
      .replace(/^\/user\/[^/]+/, '/user/:id')
      .replace(/^\/chat\/[^/]+/, '/chat/:userId')
    return `${url.origin}${pathname}`
  } catch {
    return ''
  }
}

export const getAnalyticsPageContext = (pageLocation?: string, pagePath?: string) => {
  const pageLocationUrl = sanitizeAnalyticsUrl(pageLocation || window.location.href)
    || `${window.location.origin}/`
  const pagePathUrl = sanitizeAnalyticsUrl(pagePath || pageLocationUrl) || pageLocationUrl

  return {
    page_location: pageLocationUrl,
    page_path: new URL(pagePathUrl).pathname,
    page_referrer: sanitizeAnalyticsUrl(document.referrer)
  }
}

const readStoredAttribution = (): MarketingAttribution => {
  if (typeof window === 'undefined') return {}

  try {
    const stored = window.sessionStorage.getItem(MARKETING_ATTRIBUTION_STORAGE_KEY)
    return stored ? JSON.parse(stored) as MarketingAttribution : {}
  } catch {
    return {}
  }
}

const readCurrentAttribution = (): MarketingAttribution => {
  if (typeof window === 'undefined') return {}

  const query = new URLSearchParams(window.location.search)
  return MARKETING_ATTRIBUTION_KEYS.reduce<MarketingAttribution>((attribution, key) => {
    const value = query.get(key)?.trim()
    if (value) attribution[key] = value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH)
    return attribution
  }, {})
}

export const captureMarketingAttribution = (): MarketingAttribution => {
  if (typeof window === 'undefined') return {}

  const current = readCurrentAttribution()
  const stored = readStoredAttribution()
  const attribution = Object.keys(current).length > 0 ? { ...stored, ...current } : stored

  if (Object.keys(current).length > 0) {
    try {
      window.sessionStorage.setItem(MARKETING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution))
    } catch {
      // Attribution is optional and must never block registration.
    }
  }

  return attribution
}

export const loadAnalytics = () => {
  protectInitialAuthLink()
  if (hasLoadedAnalytics || !GA_TRACKING_ID || typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }
  hasLoadedAnalytics = true

  window.dataLayer = window.dataLayer || []
  const gtag: NonNullable<Window['gtag']> = function () {
    // Google tag commands require the canonical Arguments object, not a rest array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments)
  }
  window.gtag = gtag
  gtag('js', new Date())
  const pageContext = getAnalyticsPageContext()
  gtag('set', pageContext)
  gtag('config', GA_TRACKING_ID, {
    transport_type: 'beacon',
    ...pageContext,
    // The router sends the initial and subsequent pageviews explicitly.
    // GA's separate Enhanced Measurement history setting must also be off.
    send_page_view: false
  })

  const script = document.createElement('script')
  script.async = true
  script.referrerPolicy = 'no-referrer'
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`
  script.fetchPriority = 'low'
  document.head.appendChild(script)
}

export const scheduleAnalyticsLoad = () => {
  captureMarketingAttribution()

  if (typeof window === 'undefined' || typeof document === 'undefined' || !GA_TRACKING_ID) {
    return
  }

  loadAnalytics()
}
